import React from "react"
import { Box, HStack } from "@chakra-ui/react"
import { Control, useController, useWatch } from "react-hook-form"
import TextField from "components/ReactHookForm/TextField"
import UrlField from "components/ReactHookForm/UrlField"
import PlacesAutocompleteField from "components/ReactHookForm/PlacesAutocompleteField"
import PhoneNumberField from "components/inputs/PhoneNumberField"
import Icon from "components/elements/Icon"
import {
  PassBackSectionType,
  validateBackSectionValue,
  WalletPassBuilderFormValues,
} from "./walletPassBuilderTypes"
import PassBuilderFieldInput from "./PassBuilderFieldInput"
import PassBuilderBackSectionTypeSelect from "./PassBuilderBackSectionTypeSelect"
import {
  PASS_BUILDER_BACK_COL_GAP,
  PASS_BUILDER_BACK_DELETE_COL_WIDTH,
  PASS_BUILDER_BACK_LABEL_WIDTH,
  PASS_BUILDER_BACK_TYPE_WIDTH,
  PASS_BUILDER_BACK_VALUE_WIDTH,
  PASS_BUILDER_FIELD_VARIANT,
} from "./passBuilderTokens"

const passBuilderInputProps = {
  variant: PASS_BUILDER_FIELD_VARIANT,
  fullWidth: true,
  mb: 0,
} as const

/** Hints at the input each type expects; the API normalizes the rest
 *  (missing scheme, phone formatting, address vs. lat,long). */
const VALUE_PLACEHOLDERS: Record<string, string> = {
  email: "events@example.com",
  phone: "+1 555 123 4567",
  website: "https://example.com",
  location: "Search for a place or address",
}

type SelectedPlace = {
  venue?: string
  addressLine1?: string
  city?: string
  state?: string
  zipcode?: string
  country?: string
}

/** One-line address for the pass — the API turns it into a geo: map search. */
const formatSelectedPlace = ({
  venue, addressLine1, city, state, zipcode, country,
}: SelectedPlace): string => {
  const cityLine = [[city, state].filter(Boolean).join(", "), zipcode].filter(Boolean).join(" ")

  return [venue, addressLine1, cityLine, country].filter(Boolean).join(", ")
}

type Props = {
  index: number
  control: Control<WalletPassBuilderFormValues>
  onRemove: (index: number) => void
}

const PassBuilderBackSectionRow = ({
  index, control, onRemove,
}: Props) => {
  const valueName = `backSections.${index}.value`
  const type = useWatch({ control, name: `backSections.${index}.type` as any }) as PassBackSectionType | ""
  const value = useWatch({ control, name: valueName as any }) as string
  const { field: valueField } = useController({ control: control as any, name: valueName as any })
  const valueError = validateBackSectionValue(type, value || "")

  const renderValueInput = () => {
    switch (type) {
      case "website":
        return (
          <UrlField
            name={valueName}
            control={control as any}
            variant={PASS_BUILDER_FIELD_VARIANT}
            placeholder={VALUE_PLACEHOLDERS.website}
          />
        )
      case "phone":
        return (
          // react-phone-number-input renders the country select as a sibling of the
          // input, so the flag sits outside the box. Move the field styling onto the
          // wrapper and strip it from the input to bring the flag inside.
          <Box
            w="100%"
            sx={{
              ".PhoneInput": {
                h: "40px",
                pl: 3,
                bg: "white",
                border: "1px solid",
                borderColor: valueError ? "red.500" : "gray.300",
                borderRadius: "md",
                _focusWithin: {
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                },
              },
              ".PhoneInputInput": {
                h: "auto",
                pl: 2,
                pr: 4,
                border: "none",
                bg: "transparent",
                _focusVisible: { boxShadow: "none" },
              },
            }}
          >
            <PhoneNumberField
              name={valueName}
              control={control as any}
              variant={PASS_BUILDER_FIELD_VARIANT}
              placeholder={VALUE_PLACEHOLDERS.phone}
              error={Boolean(valueError)}
              errorMessage={valueError}
            />
          </Box>
        )
      case "location":
        return (
          <Box w="100%">
            <PlacesAutocompleteField
              name={valueName}
              control={control as any}
              variant={PASS_BUILDER_FIELD_VARIANT}
              placeholder={VALUE_PLACEHOLDERS.location}
              mb={0}
              onSelect={(place: SelectedPlace) => valueField.onChange(formatSelectedPlace(place))}
            />
          </Box>
        )
      default:
        return (
          <TextField
            name={valueName as any}
            control={control as any}
            placeholder={VALUE_PLACEHOLDERS[type] || ""}
            type={type === "email" ? "email" : "text"}
            validate={((v: string) => validateBackSectionValue(type, v || "") ?? true) as any}
            error={Boolean(valueError)}
            errorText={valueError}
            {...passBuilderInputProps}
          />
        )
    }
  }

  return (
    <HStack spacing={PASS_BUILDER_BACK_COL_GAP} alignItems="center">
      <PassBuilderFieldInput width={PASS_BUILDER_BACK_TYPE_WIDTH}>
        <PassBuilderBackSectionTypeSelect
          name={`backSections.${index}.type`}
          control={control as any}
        />
      </PassBuilderFieldInput>
      <PassBuilderFieldInput width={PASS_BUILDER_BACK_LABEL_WIDTH}>
        <TextField
          name={`backSections.${index}.label` as any}
          control={control as any}
          {...passBuilderInputProps}
        />
      </PassBuilderFieldInput>
      <PassBuilderFieldInput width={PASS_BUILDER_BACK_VALUE_WIDTH}>
        {renderValueInput()}
      </PassBuilderFieldInput>
      <HStack
        w={PASS_BUILDER_BACK_DELETE_COL_WIDTH}
        flexShrink={0}
        justify="center"
      >
        <button
          type="button"
          aria-label="Remove section"
          onClick={() => onRemove(index)}
        >
          <Icon icon="delete-solid" color="red.600" />
        </button>
      </HStack>
    </HStack>
  )
}

export default PassBuilderBackSectionRow
